import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Gift, Share2, Copy, Check, UserPlus, PartyPopper } from "lucide-react";

// Costruisce il link di invito assoluto sul dominio corrente (preview o produzione),
// così la cliente condivide sempre un link funzionante.
function buildInviteLink(data) {
  const code = data?.code;
  if (code && typeof window !== "undefined") {
    return `${window.location.origin}/registrati?ref=${code}`;
  }
  return data?.link || "";
}

export function PortalReferralCard() {
  const [d, setD] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/portal/referral").then((r) => setD(r.data)).catch(() => setD(null));
  }, []);

  if (!d || d.enabled === false) return null;

  const link = buildInviteLink(d);
  const threshold = d.threshold || 3;
  const progress = Math.min(d.joined_cycle ?? 0, threshold);
  const rewardPct = d.reward_pct ?? 30;
  const welcomePct = d.new_client_discount_pct ?? 10;
  const hasReward = (d.unused_rewards_count || 0) > 0;

  const shareMessage = `Vieni da Hair Identity! Registrati con il mio invito e ricevi il ${welcomePct}% di sconto sul tuo primo servizio 💇‍♀️`;

  const doShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: "Porta un'Amica · Hair Identity", text: shareMessage, url: link });
        return;
      }
    } catch (e) { /* utente ha annullato o non supportato: fallback copia */ }
    await doCopy();
  };

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link di invito copiato!");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Impossibile copiare il link");
    }
  };

  return (
    <Card className="p-5 space-y-4" data-testid="portal-referral-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-full bg-[hsl(var(--hi-gold-soft))] flex items-center justify-center shrink-0">
            <UserPlus className="h-4.5 w-4.5 text-[hsl(var(--hi-gold))]" />
          </div>
          <div>
            <div className="font-heading text-lg font-semibold leading-tight">Porta un'Amica</div>
            <div className="text-xs text-muted-foreground">Invita e vieni premiata</div>
          </div>
        </div>
        {hasReward && (
          <Badge className="bg-[hsl(var(--hi-gold))] text-white gap-1" data-testid="portal-referral-reward-badge">
            <PartyPopper className="h-3 w-3" /> Premio -{rewardPct}%
          </Badge>
        )}
      </div>

      <p className="text-sm text-muted-foreground">
        La tua amica riceve <b className="text-foreground">-{welcomePct}%</b> sul primo servizio.
        Quando <b className="text-foreground">{threshold} amiche</b> si iscrivono, tu ottieni <b className="text-foreground">-{rewardPct}%</b>!
      </p>

      {/* Contatore progressi (avanza quando l'amica si registra) */}
      <div className="space-y-1.5" data-testid="portal-referral-progress">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Amiche iscritte</span>
          <span className="font-semibold">{progress} / {threshold}</span>
        </div>
        <div className="flex gap-1.5">
          {Array.from({ length: threshold }).map((_, i) => (
            <div key={i} data-testid={`portal-referral-dot-${i}`}
              className={`h-2 flex-1 rounded-full transition-colors ${i < progress ? "bg-[hsl(var(--hi-gold))]" : "bg-muted"}`} />
          ))}
        </div>
        {d.joined_count > 0 && (
          <div className="text-[11px] text-muted-foreground">
            {d.joined_count} amic{d.joined_count === 1 ? "a" : "he"} invitat{d.joined_count === 1 ? "a" : "e"} finora
            {d.completed_count > 0 ? ` · ${d.completed_count} con prima visita completata` : ""}
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <Button className="flex-1" onClick={doShare} data-testid="portal-referral-share">
          <Share2 className="h-4 w-4 mr-1.5" /> Condividi invito
        </Button>
        <Button variant="outline" onClick={doCopy} data-testid="portal-referral-copy" aria-label="Copia link">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>

      <div className="rounded-lg bg-muted/60 px-3 py-2 text-[11px] text-muted-foreground break-all" data-testid="portal-referral-link">
        {link}
      </div>
    </Card>
  );
}
